import { useCallback, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import { identify } from '@Analytics/identify'
import { useSubscribeToNewsletterMutation } from '@Generated/graphql'
import { generateEventID } from '@Globals/Google/DataLayer'
import { gtagEvent } from '@Globals/Google/GTag'

type SubscribeDataFormat = {
  campaignLabel?: string
  email: string
  name?: string
  source?: string
}

type UseNewsletterSubscriptionReturn = {
  loading: boolean
  subscribe: (data: SubscribeDataFormat) => Promise<boolean>
}

export const useNewsletterSubscription = (): UseNewsletterSubscriptionReturn => {
  const [performSubscribeQuery] = useSubscribeToNewsletterMutation()
  const [loading, setLoading] = useState(false)

  const subscribe = useCallback(async ({ campaignLabel = '', email, source = '' }: SubscribeDataFormat) => {
    setLoading(true)

    try {
      const result = await performSubscribeQuery({ variables: { campaign: campaignLabel, email, source } })
      const isSuccess = result.data?.subscribeToNewsletter || false

      if (!isSuccess) {
        setLoading(false)
        return false
      }

      await identify({ email })

      gtagEvent('sign_up', {
        event_id: generateEventID(),
        method: source || 'unknown',
      })

      await ampli.newsletterOptIn({
        campaign_label: campaignLabel,
        source_id: source,
      })

      setLoading(false)
      return true
    } catch {
      setLoading(false)
      return false
    }
  }, [performSubscribeQuery])

  return {
    loading,
    subscribe,
  }
}
